import { useState } from "react";
import { ShareNetwork, Link as LinkIcon, Check } from "@phosphor-icons/react";
import useFocusTrap from "@/lib/hooks/useFocusTrap";

/**
 * A small "Share this page" button for the public marketing site.
 *
 * Behavior:
 *   1. If the browser supports the Web Share API (mobile Safari/Chrome,
 *      Android Chrome, modern iPadOS), open the native share sheet with
 *      title / text / url — this is the best path for WhatsApp / iMessage
 *      / Signal / Gmail.
 *   2. Otherwise, copy the canonical URL to the clipboard and flash
 *      a short "Link copied" confirmation.
 *   3. If clipboard is also unavailable (very old browsers or iframes
 *      without the permission), show a small modal with a read-only
 *      input the user can select + copy manually.
 *
 * Props:
 *   url    — canonical URL (required)
 *   title  — page title (required for the native sheet)
 *   text   — optional short description
 *   variant — 'pill' (default, inline button) | 'ghost' (subtler)
 */
export default function ShareButton({ url, title, text, variant = "pill", className = "" }) {
  const [status, setStatus] = useState("idle");   // idle | copied | error | manual
  const [manualOpen, setManualOpen] = useState(false);

  const onShare = async (e) => {
    e.preventDefault();
    // 1. Native share sheet
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({
          title: title || document.title,
          text:  text || undefined,
          url:   url  || window.location.href,
        });
        return;
      } catch (err) {
        // User cancelled — no toast.  AbortError is the expected rejection.
        if (err && err.name !== "AbortError") {
          // fall through to copy as a second attempt
        } else {
          return;
        }
      }
    }

    // 2. Clipboard copy
    const toCopy = url || window.location.href;
    if (navigator.clipboard && window.isSecureContext !== false) {
      try {
        await navigator.clipboard.writeText(toCopy);
        setStatus("copied");
        setTimeout(() => setStatus("idle"), 2000);
        return;
      } catch {
        /* fall through */
      }
    }

    // 3. Legacy execCommand fallback (iframes / old Safari)
    try {
      const ta = document.createElement("textarea");
      ta.value = toCopy;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.top = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setStatus("copied");
      setTimeout(() => setStatus("idle"), 2000);
      return;
    } catch {
      /* ignore */
    }

    // 4. Last resort — manual copy modal
    setManualOpen(true);
  };

  const pillClasses =
    variant === "ghost"
      ? "inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 transition hover:bg-neutral-50"
      : "inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold text-white shadow-sm transition hover:brightness-105";

  const style =
    variant === "ghost"
      ? undefined
      : { background: "var(--color-primary)" };

  return (
    <>
      <button
        type="button"
        onClick={onShare}
        data-testid="share-button"
        data-state={status}
        className={`${pillClasses} ${className}`.trim()}
        style={style}
        aria-label={status === "copied" ? "Link copied" : "Share this page"}
      >
        {status === "copied" ? (
          <>
            <Check size={12} weight="bold" />
            Link copied
          </>
        ) : (
          <>
            <ShareNetwork size={12} weight="bold" />
            Share
          </>
        )}
      </button>

      {manualOpen && (
        <ShareManualDialog
          url={url}
          onClose={() => setManualOpen(false)}
        />
      )}
    </>
  );
}


/**
 * Manual-copy fallback dialog — extracted so the focus trap hook only
 * mounts while the dialog is open.
 */
function ShareManualDialog({ url, onClose }) {
  const trapRef = useFocusTrap({ active: true, onEscape: onClose });
  return (
    <div
      data-testid="share-button-manual"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-5"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={trapRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-manual-title"
        className="w-full max-w-md rounded-2xl border border-neutral-200 bg-white p-6 shadow-xl"
      >
        <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
          <LinkIcon size={12} weight="duotone" className="mr-1 inline" />
          Copy link
        </p>
        <p id="share-manual-title" className="mt-1 font-heading text-lg font-semibold text-neutral-900">
          Select and copy this link
        </p>
        <input
          type="text"
          readOnly
          value={url || (typeof window !== "undefined" ? window.location.href : "")}
          onFocus={(e) => e.target.select()}
          className="mt-4 w-full rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm text-neutral-900 focus:border-primary focus:outline-none"
          data-testid="share-button-manual-input"
        />
        <div className="mt-5 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-neutral-200 bg-white px-4 py-1.5 text-xs font-semibold text-neutral-700 hover:bg-neutral-50"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
